import { type RefObject, useEffect, useRef } from 'react';

const isInside = (
  sheetRef: RefObject<HTMLElement | null>,
  target: EventTarget | null,
) => target instanceof Node && !!sheetRef.current?.contains(target);

const isDocumentScrollTarget = (target: EventTarget | null) =>
  target === document ||
  target === document.documentElement ||
  target === document.body;

/**
 * Close challenger mobile bottom sheets when the user scrolls or interacts
 * outside the drawer (Funnel-Feedback booking summary mobile behavior).
 *
 * Ignores synthetic document/body scroll from `useModalFocus` body lock
 * (`position: fixed`), which otherwise opens-then-immediately-closes the sheet.
 */
export function useCloseMobileDrawerOnOutsideInteraction(
  isOpen: boolean,
  sheetRef: RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    // Body scroll lock can emit scroll on the same turn the drawer mounts.
    // Wait until after paint before treating outside events as user intent.
    let enabled = false;
    const enableId = window.requestAnimationFrame(() => {
      enabled = true;
    });

    const closeIfOutside = (event: Event) => {
      if (!enabled) return;
      // Page scroll from position:fixed lock — not user scrolling the sheet.
      if (event.type === 'scroll' && isDocumentScrollTarget(event.target)) {
        return;
      }
      if (!isInside(sheetRef, event.target)) {
        onCloseRef.current();
      }
    };

    document.addEventListener('scroll', closeIfOutside, true);
    document.addEventListener('wheel', closeIfOutside, {
      capture: true,
      passive: true,
    });
    document.addEventListener('touchmove', closeIfOutside, {
      capture: true,
      passive: true,
    });
    document.addEventListener('pointerdown', closeIfOutside, true);

    return () => {
      window.cancelAnimationFrame(enableId);
      document.removeEventListener('scroll', closeIfOutside, true);
      document.removeEventListener('wheel', closeIfOutside, true);
      document.removeEventListener('touchmove', closeIfOutside, true);
      document.removeEventListener('pointerdown', closeIfOutside, true);
    };
  }, [isOpen, sheetRef]);
}
